import type { ScheduleTourFormValues } from './types'
import type { MutableRefObject, RefObject } from 'react'
import type { UserProfile } from 'store/users/types'

import { Formik } from 'formik'
import isEmpty from 'lodash/isEmpty'
import { useState, useRef, useEffect } from 'react'
import { OverlayTrigger } from 'react-bootstrap'
import { useMedia } from 'react-use'

import {
  Checkbox,
  NumberFormatInputWithLabel,
  TextAreaWithLabel,
  TextInputWithLabel,
} from 'components'

import { PLACEHOLDER } from './constants'
import {
  ScheduleTourFormStyled,
  ContinueButton,
  BtnWithTooltipWrapper,
  MessageInputSection,
  TooltipStyled,
} from './schedule-tour-form.style'
import { TermBox } from './term-box'
import { ScheduleTourModalFormValidation } from './validations'

function useOutsideDetector(
  ref: RefObject<MutableRefObject<HTMLDivElement | null>>,
  onOutsideClick: () => void,
) {
  useEffect(() => {
    function handleClickOutside(event: { target: HTMLInputElement }) {
      const htmlTarget: HTMLDivElement = ref.current as unknown as HTMLDivElement
      if (ref.current && !htmlTarget.contains(event.target)) {
        onOutsideClick()
      }
    }
    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener)
    }
  }, [onOutsideClick, ref])
}

interface ScheduleTourFormProps {
  handleSubmit: (values: ScheduleTourFormValues) => void
  defaultMessage: string
  buttonText?: string
  isDateSelected?: boolean
  profile?: UserProfile
  formData?: ScheduleTourFormValues
}

export const ScheduleTourForm: React.FC<ScheduleTourFormProps> = ({
  handleSubmit,
  defaultMessage,
  buttonText = 'Continue',
  isDateSelected = false,
  profile = {},
  formData,
}) => {
  const [mobileTooltipStatus, setMobileTooltipStatus] = useState<boolean>()
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout>()
  const tooltipWrapperRef = useRef<MutableRefObject<HTMLDivElement | null>>(null)
  useOutsideDetector(tooltipWrapperRef, () => {
    clearTimeout(tooltipTimeout as NodeJS.Timeout)
    setMobileTooltipStatus(false)
  })
  const closeTooltipRef = useRef<HTMLDivElement>(null)
  const isMobile = useMedia('(max-width: 991px)')
  const isLoggedIn = !isEmpty(profile)
  const loggedInInitialValues: ScheduleTourFormValues = {
    fullName: profile?.fullName || '',
    email: profile?.email || '',
    phone: profile?.phone || undefined,
    message: formData?.message ? formData?.message : '',
    isAccept: false,
  }
  const guestInitialValues: ScheduleTourFormValues = {
    fullName: formData?.fullName || '',
    email: formData?.email || '',
    phone: formData?.phone || undefined,
    message: formData?.message ? formData?.message : '',
    isAccept: false,
  }

  const renderTooltip = (
    <TooltipStyled id="schedule-tour-tooltip">
      {isLoggedIn
        ? 'Please select at least one preferred date'
        : 'Please complete all required fields'}
    </TooltipStyled>
  )

  const hideTooltipWithDelay = () => {
    clearTimeout(tooltipTimeout as NodeJS.Timeout)
    const myTimeOut = setTimeout(() => {
      setMobileTooltipStatus(false)
      closeTooltipRef.current?.click()
    }, 3000)
    setTooltipTimeout(myTimeOut)
  }

  return (
    <Formik
      initialValues={isLoggedIn ? loggedInInitialValues : guestInitialValues}
      validationSchema={ScheduleTourModalFormValidation}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleBlur, setFieldValue, values, errors, touched }) => (
        <ScheduleTourFormStyled>
          <TextInputWithLabel
            $display={profile?.fullName ? 'none' : 'block'}
            id="senderFullName"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.fullName}
            error={touched?.fullName && errors?.fullName}
            name="fullName"
            label="First and Last Name"
            placeholder={PLACEHOLDER.FULL_NAME}
            isRequired
            data-testid="schedule-name-test-id"
          />
          <TextInputWithLabel
            $display={profile?.email ? 'none' : 'block'}
            id="senderEmail"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            error={touched?.email && errors?.email}
            name="email"
            label="Email"
            placeholder={PLACEHOLDER.EMAIL}
            isRequired
          />
          <NumberFormatInputWithLabel
            $display={isLoggedIn ? 'none' : 'block'}
            id="senderPhone"
            type="phone"
            onChange={(target) => {
              setFieldValue('phone', target.value)
            }}
            onBlur={handleBlur}
            value={values.phone}
            error={touched?.phone && errors?.phone}
            name="phone"
            label="Phone"
            placeholder={PLACEHOLDER.PHONE}
          />
          <MessageInputSection>
            <TextAreaWithLabel
              id="message"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.message}
              name="message"
              label="Message"
              placeholder={defaultMessage}
              onFocus={(e) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (e.target?.value === '') {
                  setFieldValue('message', defaultMessage)
                }
              }}
              $borderRadius="8px"
            />
          </MessageInputSection>
          {values.phone && !isLoggedIn && (
            <Checkbox
              id="checkbox"
              onCheck={() => {
                const checkboxValue = !values.isAccept
                setFieldValue('isAccept', checkboxValue)
              }}
              name="isAccept"
              text={<TermBox />}
              checked={values.isAccept}
              $margin="16px 0 16px 0"
            />
          )}
          <div ref={closeTooltipRef} id="close-tooltip-ref" style={{ display: 'none' }} />
          <OverlayTrigger
            trigger={!isMobile ? ['hover', 'focus'] : ['click']}
            show={isMobile ? mobileTooltipStatus : undefined}
            placement="top"
            delay={{ show: 0, hide: 0 }}
            overlay={
              !(isLoggedIn || !values?.phone ? true : values.isAccept) ||
              !isDateSelected ||
              values.fullName.length < 1 ||
              values.email.length < 1 ||
              errors?.email !== undefined ||
              errors?.phone !== undefined ? (
                renderTooltip
              ) : (
                <></>
              )
            }
            onEntered={() => {
              if (isMobile) {
                hideTooltipWithDelay()
              }
            }}
            onExit={() => {
              if (isMobile) {
                clearTimeout(tooltipTimeout as NodeJS.Timeout)
              }
            }}
            onToggle={(status) => {
              if (isMobile) {
                if (status) {
                  setMobileTooltipStatus(true)
                } else {
                  setMobileTooltipStatus(false)
                }
              }
            }}
          >
            <BtnWithTooltipWrapper
              isDisabled={
                !(isLoggedIn || !values?.phone ? true : values.isAccept) ||
                !isDateSelected ||
                values.fullName.length < 1 ||
                values.email.length < 1 ||
                errors?.email !== undefined ||
                errors?.phone !== undefined
              }
              ref={tooltipWrapperRef as unknown as RefObject<HTMLDivElement>}
            >
              <ContinueButton
                id="continueButton"
                type="submit"
                btnSize="lg"
                mBtnSize="md"
                disabled={
                  !(isLoggedIn || !values?.phone ? true : values.isAccept) ||
                  !isDateSelected ||
                  values.fullName.length < 1 ||
                  values.email.length < 1 ||
                  errors?.email !== undefined ||
                  errors?.phone !== undefined
                }
              >
                {buttonText}
              </ContinueButton>
            </BtnWithTooltipWrapper>
          </OverlayTrigger>
        </ScheduleTourFormStyled>
      )}
    </Formik>
  )
}
